import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import { Typography, Button, Chip, Divider } from '@mui/material';
import { Icon, BasicTooltip, SimpleModal, TableLoader, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import { SubscriptionTierBadge } from 'src/components/SubscriptionTier';
import { tierOf } from 'src/components/SubscriptionTier/tiers';
import { dateToFormat } from 'src/utils/date';
import useStyles from './styles';

/**
 * A company's bill-intake email address (LB-5770, LB-5810): provision it, copy it, rotate it, and
 * read the full alias history with statuses. Rendered as a modal so it behaves the same whether it
 * is opened from the admin PM-accounts grid or from the Bills page "Bill automation settings"
 * button — one focused surface for the one thing it does.
 *
 * Email intake is a paid feature, so nothing is provisioned implicitly — not at company creation,
 * not by reading. A company with no address shows the empty state and an explicit Generate action,
 * which is the moment the feature is turned on for them. Both provisioning and rotation are
 * staff-only server-side; every surface that mounts this is already staff-gated.
 */
// Backend statuses → house chip variants. Rotated is visually distinct from Expired because a
// rotated address is still routing (inside its grace window) — an operator must be able to tell
// "still works, update the utilities" from "dead".
const STATUS_CHIP_VARIANT = {
  Active: 'success',
  Rotated: 'info',
  Expired: 'inactive'
};
export const BillIntakeAliasModal = observer(_ref => {
  let company = _ref.company,
    onClose = _ref.onClose;
  const store = useStore();
  const classes = useStyles();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isLoading = _useState2[0],
    setIsLoading = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    alias = _useState4[0],
    setAlias = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    history = _useState6[0],
    setHistory = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    rotateConfirmOpen = _useState8[0],
    setRotateConfirmOpen = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isProvisioning = _useState0[0],
    setIsProvisioning = _useState0[1];
  // The plan's name when it blocks email intake (LB-6229); null otherwise.
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    lockedTier = _useState10[0],
    setLockedTier = _useState10[1];
  // Read on render, so the modal fetches again if the flags arrive after it opened.
  const isBillingOptionsEnabled = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled;
  const isOpen = !!company;
  // The company whose responses we are still willing to accept. A fetch awaits several calls in turn, so a
  // close or a company switch can land between them, and a late response would otherwise repopulate
  // the modal with the previous company's address.
  const activeCompanyId = useRef(null);

  // Still the company this modal is showing? Guards every await below: a close or a company
  // switch flips activeCompanyId.current, so a late response neither fires the next request nor
  // writes state/toasts for a company the modal has moved on from.
  const isStillActive = companyId => activeCompanyId.current === companyId;

  // Counts fetches, so only the newest writes state: the flags arriving starts a second fetch for the same company,
  // and a slower first one must not land after it.
  const fetchGeneration = useRef(0);
  const fetchData = async companyId => {
    var _await$tierRead, _plan$allows;
    activeCompanyId.current = companyId;
    const generation = ++fetchGeneration.current;
    const isCurrent = () => isStillActive(companyId) && fetchGeneration.current === generation;
    setIsLoading(true);
    const aliasResp = await store.company.getBillIntakeAliasAsync.run({
      companyId
    });
    // Bail before the second request too, not just before the state write, so a switch between the
    // two awaits doesn't spend a history call (and risk its error toast) on the old company.
    if (!isCurrent()) return;
    const currentAlias = (aliasResp === null || aliasResp === void 0 ? void 0 : aliasResp.response) || null;
    // With billing options on, a new address needs Plus or Autopilot (LB-6229). The plan is read only for a company
    // with no address, to say why Generate is off; the server enforces it either way, so a failed read changes nothing.
    // It's read alongside the history.
    const tierRead = !currentAlias && isBillingOptionsEnabled ? store.company.getSubscriptionTierSilentAsync.run({
      companyId
    }) : null;
    const historyResp = await store.company.getBillIntakeAliasHistoryAsync.run({
      companyId
    });
    const plan = tierRead ? (_await$tierRead = await tierRead) === null || _await$tierRead === void 0 ? void 0 : _await$tierRead.response : null;
    if (!isCurrent()) return;
    const tierLock = (plan === null || plan === void 0 || (_plan$allows = plan.allows) === null || _plan$allows === void 0 ? void 0 : _plan$allows.emailIn) === false ? plan.tier : null;
    setAlias(currentAlias);
    setHistory((historyResp === null || historyResp === void 0 ? void 0 : historyResp.response) || []);
    setLockedTier(tierLock);
    setIsLoading(false);
  };
  useEffect(() => {
    // Rotation is destructive and reads company at submit time, so a confirmation left open across
    // a switch could rotate a company the admin never opened it for.
    setRotateConfirmOpen(false);
    if (company !== null && company !== void 0 && company.companyId) {
      fetchData(company.companyId);
    } else {
      activeCompanyId.current = null;
      setAlias(null);
      setHistory([]);
      setLockedTier(null);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company === null || company === void 0 ? void 0 : company.companyId, isBillingOptionsEnabled]);
  const handleCopy = address => {
    navigator.clipboard.writeText(address).then(() => {
      toast('success', {
        body: ['Successfully copied to clipboard!']
      });
    }).catch(error => {
      toast('error', {
        body: ['Error copying to clipboard', error]
      });
    });
  };
  const handleProvision = async () => {
    var _resp$response;
    const companyId = company.companyId;
    setIsProvisioning(true);
    const resp = await store.company.provisionBillIntakeAliasAsync.run({
      companyId
    });
    setIsProvisioning(false);
    if (!resp || !resp.isSuccessful) return;
    // Before the guard below, not after it: the address exists now whether or not this modal is
    // still open, and the Bills page banner has to hear about it either way.
    store.company.noteBillIntakeAliasChanged(companyId, resp.response);
    // The modal may have closed or switched company during the request; don't toast success or
    // refetch for a company it is no longer showing.
    if (!isStillActive(companyId)) return;
    toast('success', {
      body: ["Email intake enabled: ".concat((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.address)]
    });
    await fetchData(companyId);
  };
  const handleRotateConfirm = async _ref2 => {
    var _resp$response2;
    let action = _ref2.action;
    setRotateConfirmOpen(false);
    if (action !== 'submit') return;
    const companyId = company.companyId;
    const resp = await store.company.rotateBillIntakeAliasAsync.run({
      companyId
    });
    // Repo convention: check isSuccessful and return silently on failure — the async layer surfaces
    // the error toast itself, so a second one here would double up.
    if (!resp || !resp.isSuccessful) return;
    // The old address is retired on the server now, so this goes out even if the modal closed while
    // the request was in flight -- that is exactly when a banner left showing it would be missed.
    store.company.noteBillIntakeAliasChanged(companyId, resp.response);
    // Same guard as provisioning: a close/switch mid-request must not repopulate or toast here.
    if (!isStillActive(companyId)) return;
    toast('success', {
      body: ["New address issued: ".concat((_resp$response2 = resp.response) === null || _resp$response2 === void 0 ? void 0 : _resp$response2.address)]
    });
    await fetchData(companyId);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SimpleModal, {
    title: "Bill intake address",
    subTitle: company === null || company === void 0 ? void 0 : company.companyName,
    isOpen: isOpen,
    handleClose: onClose,
    noFooter: true
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.modalBody
  }, isLoading ? /*#__PURE__*/React.createElement(TableLoader, null) : /*#__PURE__*/React.createElement(React.Fragment, null, alias !== null && alias !== void 0 && alias.address ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.addressRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.addressText
  }, alias.address), /*#__PURE__*/React.createElement(BasicTooltip, {
    title: "Copy to clipboard",
    placement: "top"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    onClick: () => handleCopy(alias.address)
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "document-file-copy"
  })))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    onClick: () => setRotateConfirmOpen(true)
  }, "Rotate address")), /*#__PURE__*/React.createElement(Typography, {
    className: classes.graceNote
  }, "Rotating issues a new address. The current one keeps working for 30 days so the company can update every utility provider they gave it to.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, null, "Email intake is not enabled for this company."), lockedTier &&
  /*#__PURE__*/
  // The plan's badge (LB-6232); a plan name the badge doesn't know stays a plain word.
  React.createElement(Typography, {
    className: classes.tierLockNote
  }, "Email intake needs Plus or Autopilot. ", (company === null || company === void 0 ? void 0 : company.companyName) || 'This company', " is on", ' ', tierOf(lockedTier) ? /*#__PURE__*/React.createElement(SubscriptionTierBadge, {
    tier: lockedTier
  }) : lockedTier, "."), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleProvision,
    disabled: isProvisioning || !!lockedTier
  }, "Generate address")), /*#__PURE__*/React.createElement(Typography, {
    className: classes.graceNote
  }, "Generating turns email intake on for this company and gives them an address to send bills to. Do this once they are paying for the feature.")), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "History"), history.length ? history.map(item => /*#__PURE__*/React.createElement("div", {
    className: classes.historyRow,
    key: item.tag
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.addressText
  }, item.address), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: item.status,
    variant: STATUS_CHIP_VARIANT[item.status] || 'info'
  }), !!item.rotatedAt && /*#__PURE__*/React.createElement(BasicTooltip, {
    title: "Rotated on",
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.graceNote
  }, dateToFormat(item.rotatedAt))))) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "No history yet.")))), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: "Rotate bill intake address",
    subTitleText: company === null || company === void 0 ? void 0 : company.companyName,
    bodyText: 'The current address keeps working for 30 days. ' + "".concat((company === null || company === void 0 ? void 0 : company.companyName) || 'The company', " must update every utility provider they gave it to. ") + 'This cannot be undone.',
    externalIsOpen: rotateConfirmOpen,
    objectToPassOver: company,
    callBack: handleRotateConfirm,
    customTrigger: null
  }));
});